import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
export function InfiniteCanvas({ children, onViewportChange, onNodeDrag, showControls = true, minZoom = 0.5, maxZoom = 3, gridSize = 20, snapToGrid = false }) {
    const containerRef = useRef(null);
    const canvasRef = useRef(null);
    const [viewport, setViewport] = useState({ x: 0, y: 0, zoom: 1 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [isSpacePressed, setIsSpacePressed] = useState(false);
    // Update parent when viewport changes
    useEffect(() => {
        onViewportChange?.(viewport);
    }, [viewport, onViewportChange]);
    // Handle wheel zoom
    const handleWheel = useCallback((e) => {
        if (!containerRef.current)
            return;
        e.preventDefault();
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        const newZoom = Math.min(Math.max(viewport.zoom * delta, minZoom), maxZoom);
        // Get mouse position relative to container
        const rect = containerRef.current.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        // Calculate zoom point in world coordinates
        const worldX = (mouseX - viewport.x) / viewport.zoom;
        const worldY = (mouseY - viewport.y) / viewport.zoom;
        // Adjust pan to keep mouse position fixed
        const newX = mouseX - worldX * newZoom;
        const newY = mouseY - worldY * newZoom;
        setViewport({ x: newX, y: newY, zoom: newZoom });
    }, [viewport, minZoom, maxZoom]);
    // Handle mouse drag for panning
    const handleMouseDown = (e) => {
        if (e.button === 1 || (e.button === 0 && isSpacePressed)) {
            setIsDragging(true);
            setDragStart({ x: e.clientX - viewport.x, y: e.clientY - viewport.y });
        }
    };
    const handleMouseMove = (e) => {
        if (isDragging) {
            setViewport(prev => ({
                ...prev,
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y
            }));
        }
    };
    const handleMouseUp = () => {
        setIsDragging(false);
    };
    // Keyboard shortcuts
    const handleKeyDown = (e) => {
        if (e.code === 'Space') {
            setIsSpacePressed(true);
            e.preventDefault();
        }
        // Zoom shortcuts
        if (e.key === '+' || e.key === '=') {
            setViewport(prev => ({
                ...prev,
                zoom: Math.min(prev.zoom * 1.1, maxZoom)
            }));
        }
        if (e.key === '-' || e.key === '_') {
            setViewport(prev => ({
                ...prev,
                zoom: Math.max(prev.zoom * 0.9, minZoom)
            }));
        }
        // Pan with arrow keys
        const panSpeed = 20;
        if (e.key === 'ArrowUp') {
            setViewport(prev => ({ ...prev, y: prev.y + panSpeed }));
        }
        if (e.key === 'ArrowDown') {
            setViewport(prev => ({ ...prev, y: prev.y - panSpeed }));
        }
        if (e.key === 'ArrowLeft') {
            setViewport(prev => ({ ...prev, x: prev.x + panSpeed }));
        }
        if (e.key === 'ArrowRight') {
            setViewport(prev => ({ ...prev, x: prev.x - panSpeed }));
        }
        // Reset view
        if ((e.ctrlKey || e.metaKey) && e.key === '0') {
            setViewport({ x: 0, y: 0, zoom: 1 });
        }
    };
    const handleKeyUp = (e) => {
        if (e.code === 'Space') {
            setIsSpacePressed(false);
        }
    };
    // Attach wheel listener
    useEffect(() => {
        const container = containerRef.current;
        if (!container)
            return;
        container.addEventListener('wheel', handleWheel, { passive: false });
        return () => container.removeEventListener('wheel', handleWheel);
    }, [handleWheel]);
    // Attach keyboard listeners
    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, [minZoom, maxZoom]);
    const handleZoomIn = () => {
        setViewport(prev => ({
            ...prev,
            zoom: Math.min(prev.zoom * 1.2, maxZoom)
        }));
    };
    const handleZoomOut = () => {
        setViewport(prev => ({
            ...prev,
            zoom: Math.max(prev.zoom * 0.83, minZoom)
        }));
    };
    const handleReset = () => {
        setViewport({ x: 0, y: 0, zoom: 1 });
    };
    return (_jsxs("div", { ref: containerRef, className: "relative w-full h-full overflow-hidden bg-gray-50 dark:bg-gray-900 cursor-grab active:cursor-grabbing", onMouseDown: handleMouseDown, onMouseMove: handleMouseMove, onMouseUp: handleMouseUp, onMouseLeave: handleMouseUp, style: { touchAction: 'none' }, children: [_jsxs("div", { ref: canvasRef, className: "absolute inset-0", style: {
                    transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
                    transformOrigin: '0 0',
                    transition: isDragging ? 'none' : 'transform 0.1s ease-out'
                }, children: [snapToGrid && (_jsx("div", { className: "absolute inset-0 pointer-events-none", style: {
                            backgroundImage: `
                linear-gradient(rgba(200, 200, 200, 0.1) 1px, transparent 1px),
                linear-gradient(90deg, rgba(200, 200, 200, 0.1) 1px, transparent 1px)
              `,
                            backgroundSize: `${gridSize}px ${gridSize}px`,
                            zIndex: -1
                        } })), _jsx("div", { className: "relative", children: children })] }), showControls && (_jsxs("div", { className: "absolute bottom-6 right-6 flex items-center gap-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-2 z-50", children: [_jsx("button", { onClick: handleZoomOut, className: "p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors", title: "Zoom Out (- key)", children: _jsx(ZoomOut, { className: "w-4 h-4 text-gray-600 dark:text-gray-400" }) }), _jsxs("div", { className: "text-xs font-medium text-gray-600 dark:text-gray-400 min-w-12 text-center", children: [Math.round(viewport.zoom * 100), "%"] }), _jsx("button", { onClick: handleZoomIn, className: "p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors", title: "Zoom In (+ key)", children: _jsx(ZoomIn, { className: "w-4 h-4 text-gray-600 dark:text-gray-400" }) }), _jsx("div", { className: "w-px h-6 bg-gray-300 dark:bg-gray-600" }), _jsx("button", { onClick: handleReset, className: "p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors", title: "Reset View (Ctrl+0)", children: _jsx(RotateCcw, { className: "w-4 h-4 text-gray-600 dark:text-gray-400" }) })] })), isSpacePressed && (_jsx("div", { className: "absolute top-4 left-4 text-xs text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 px-2 py-1 rounded pointer-events-none", children: "Drag to pan" }))] }));
}
